import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Search,
  Download,
  Loader2,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  History,
  FileText,
} from 'lucide-react';
import {
  apiService,
  BillingOverviewItem,
  BillingOverviewResponse,
  BillingOverviewParams,
  BillingPeriodSnapshot,
  PhoneNumberPurchase,
  BillingHistoryResponse,
  MonthlyBillingBreakdown,
} from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatSecondsAsMmSs, secondsToBilledMinutes } from '../../utils/formatDuration';
import { toast } from 'sonner';

// ── Helper functions ──────────────────────────────────────────────

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE');
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Aktiv</Badge>;
    case 'cancelled':
      return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Gekuendigt</Badge>;
    case 'expired':
      return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Abgelaufen</Badge>;
    case 'suspended':
      return <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">Pausiert</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function exportToCsv(items: BillingOverviewItem[], filename: string): void {
  const header = [
    'Kunde',
    'Plan',
    'Status',
    'Waehrung',
    'Grundgebuehr (Cent)',
    'Preis/Min (Cent)',
    'Preis/Chat (Cent)',
    'Minuten',
    'Konversationen',
    'Testruns',
    'Kosten Minuten (Cent)',
    'Kosten Konversationen (Cent)',
    'Telefonnummern (Anzahl)',
    'Monatspreis/Nummer (Cent)',
    'Kosten Telefonnummern (Cent)',
    'Ext. Weiterleitungen',
    'Preis/Weiterleitung (Cent)',
    'Kosten Ext. Weiterleitungen (Cent)',
    'Gesamt (Cent)',
    'Gesamt (EUR)',
    'Zeitraum Start',
    'Zeitraum Ende',
    'Rechnungsadresse',
  ].join(';');

  const rows = items.map((item) => {
    const billedMinutes = secondsToBilledMinutes(item.usedCallSeconds);
    const minuteCosts = billedMinutes * item.pricePerMinuteInCents;
    const convCosts = item.usedConversations * item.pricePerChatInCents;
    const fwdCosts = (item.usedExternalForwardings ?? 0) * (item.pricePerExternalForwardingInCents ?? 0);
    const billingAddress = item.billingAddress
      ? [
          item.billingAddress.company,
          item.billingAddress.name,
          item.billingAddress.street,
          `${item.billingAddress.zip} ${item.billingAddress.city}`,
          item.billingAddress.country,
        ]
          .filter(Boolean)
          .join(', ')
      : '';

    return [
      item.customerName,
      item.planName,
      item.status,
      item.currency,
      item.baseFeeInCents,
      item.pricePerMinuteInCents,
      item.pricePerChatInCents,
      billedMinutes,
      item.usedConversations,
      item.usedTestRuns,
      minuteCosts,
      convCosts,
      item.activePhoneNumbers,
      item.phoneMonthlyFeeInCents,
      item.activePhoneNumbers * item.phoneMonthlyFeeInCents,
      item.usedExternalForwardings ?? 0,
      item.pricePerExternalForwardingInCents ?? 0,
      fwdCosts,
      item.calculatedTotalInCents,
      formatCents(item.calculatedTotalInCents),
      new Date(item.currentPeriodStart).toLocaleDateString('de-DE'),
      new Date(item.currentPeriodEnd).toLocaleDateString('de-DE'),
      billingAddress,
    ].join(';');
  });

  const csvContent = '\uFEFF' + [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// ── Types ──────────────────────────────────────────────

type SortField = 'customerName' | 'planName' | 'status' | 'baseFee' | 'total' | 'periodEnd';

// ── Component ──────────────────────────────────────────────

export function BillingPage() {
  const [data, setData] = useState<BillingOverviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('customerName');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailItem, setDetailItem] = useState<BillingOverviewItem | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Detail dialog state
  const [billingHistory, setBillingHistory] = useState<BillingHistoryResponse | null>(null);
  const [phonePurchases, setPhonePurchases] = useState<PhoneNumberPurchase[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('current');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Monthly breakdown state
  const [activeTab, setActiveTab] = useState<string>('periods');
  const [breakdownData, setBreakdownData] = useState<MonthlyBillingBreakdown | null>(null);
  const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false);
  const [breakdownYear, setBreakdownYear] = useState(new Date().getFullYear());
  const [breakdownMonth, setBreakdownMonth] = useState(new Date().getMonth() + 1);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: BillingOverviewParams = {
        page,
        limit,
        sortBy,
        sortDirection,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      const result = await apiService.getBillingOverview(params);
      setData(result);
    } catch (error) {
      console.error('Failed to load billing overview:', error);
      toast.error(extractErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, sortBy, sortDirection, debouncedSearch, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset page when filters/sort change (except page itself)
  useEffect(() => {
    setPage(1);
  }, [statusFilter, sortBy, sortDirection]);

  // Load billing history and phone purchases when detail dialog opens
  useEffect(() => {
    if (!detailItem) {
      setBillingHistory(null);
      setPhonePurchases([]);
      setSelectedPeriod('current');
      setBreakdownData(null);
      setActiveTab('periods');
      return;
    }

    setIsLoadingHistory(true);
    Promise.all([
      apiService.getBillingHistory(detailItem.customerId),
      apiService.getPhoneNumberPurchases(detailItem.customerId),
    ]).then(([history, purchases]) => {
      setBillingHistory(history);
      setPhonePurchases(purchases);
    }).catch((err) => {
      console.error('Failed to load billing details:', err);
    }).finally(() => {
      setIsLoadingHistory(false);
    });
  }, [detailItem]);

  // Load monthly breakdown when tab is active
  const loadBreakdown = useCallback(async () => {
    if (!detailItem) return;
    setIsLoadingBreakdown(true);
    try {
      const result = await apiService.getMonthlyBillingBreakdown(
        detailItem.customerId, breakdownYear, breakdownMonth
      );
      setBreakdownData(result);
    } catch (error) {
      toast.error(extractErrorMessage(error));
      setBreakdownData(null);
    } finally {
      setIsLoadingBreakdown(false);
    }
  }, [detailItem, breakdownYear, breakdownMonth]);

  useEffect(() => {
    if (activeTab === 'breakdown' && detailItem) {
      loadBreakdown();
    }
  }, [activeTab, loadBreakdown]);

  // ── Sort handling ──

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(field);
      setSortDirection('ASC');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 ml-1 inline-block text-gray-400" />;
    if (sortDirection === 'ASC') return <ArrowUp className="w-3 h-3 ml-1 inline-block text-blue-600" />;
    return <ArrowDown className="w-3 h-3 ml-1 inline-block text-blue-600" />;
  };

  // ── Selection handling ──

  const items = data?.items ?? [];
  const allOnPageSelected = items.length > 0 && items.every((item) => selectedIds.has(item.subscriptionId));

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      const next = new Set(selectedIds);
      items.forEach((item) => next.delete(item.subscriptionId));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      items.forEach((item) => next.add(item.subscriptionId));
      setSelectedIds(next);
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  // ── Export handling ──

  const handleExportSelected = () => {
    const selectedItems = items.filter((item) => selectedIds.has(item.subscriptionId));
    if (selectedItems.length === 0) return;
    exportToCsv(selectedItems, `abrechnungen-auswahl-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const params: BillingOverviewParams = {
        page: 1,
        limit: 10000,
        sortBy,
        sortDirection,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      const result = await apiService.getBillingOverview(params);
      exportToCsv(result.items, `abrechnungen-alle-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(extractErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  };

  // ── Pagination ──

  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  // ── Render ──

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Abrechnungen</h1>
            <p className="text-gray-600">Abrechnungsuebersicht aller Kunden</p>
          </div>
          <div className="flex items-center space-x-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    onClick={handleExportSelected}
                    disabled={selectedIds.size === 0}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Auswahl exportieren
                  </Button>
                </span>
              </TooltipTrigger>
              {selectedIds.size === 0 && (
                <TooltipContent>
                  <p>Bitte mindestens einen Eintrag auswaehlen</p>
                </TooltipContent>
              )}
            </Tooltip>
            <Button onClick={handleExportAll} disabled={isExporting}>
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Alle exportieren
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Kunde suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={statusFilter || 'all'}
            onValueChange={(value) => {
              setStatusFilter(value === 'all' ? '' : value);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Status filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="cancelled">Gekuendigt</SelectItem>
              <SelectItem value="expired">Abgelaufen</SelectItem>
              <SelectItem value="suspended">Pausiert</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex items-center justify-center h-40">
                <p className="text-gray-500">Keine Abrechnungen gefunden</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allOnPageSelected}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Alle auswaehlen"
                        />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none whitespace-nowrap"
                        onClick={() => handleSort('customerName')}
                      >
                        Kunde
                        <SortIcon field="customerName" />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none whitespace-nowrap"
                        onClick={() => handleSort('planName')}
                      >
                        Plan
                        <SortIcon field="planName" />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none whitespace-nowrap"
                        onClick={() => handleSort('status')}
                      >
                        Status
                        <SortIcon field="status" />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none whitespace-nowrap"
                        onClick={() => handleSort('baseFee')}
                      >
                        Grundgebuehr
                        <SortIcon field="baseFee" />
                      </TableHead>
                      <TableHead className="whitespace-nowrap">Minuten</TableHead>
                      <TableHead className="whitespace-nowrap">Konversationen</TableHead>
                      <TableHead className="whitespace-nowrap">Telefonnummern</TableHead>
                      <TableHead
                        className="cursor-pointer select-none whitespace-nowrap"
                        onClick={() => handleSort('total')}
                      >
                        Gesamt
                        <SortIcon field="total" />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none whitespace-nowrap"
                        onClick={() => handleSort('periodEnd')}
                      >
                        Zeitraum endet
                        <SortIcon field="periodEnd" />
                      </TableHead>
                      <TableHead className="w-16">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.subscriptionId}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(item.subscriptionId)}
                            onCheckedChange={() => toggleSelect(item.subscriptionId)}
                            aria-label={`${item.customerName} auswaehlen`}
                          />
                        </TableCell>
                        <TableCell className="font-medium whitespace-nowrap">
                          {item.customerName}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{item.planName}</TableCell>
                        <TableCell>{getStatusBadge(item.status)}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatCents(item.baseFeeInCents)} {item.currency}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {secondsToBilledMinutes(item.usedCallSeconds)} x {formatCents(item.pricePerMinuteInCents)} {item.currency}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {item.usedConversations} x {formatCents(item.pricePerChatInCents)} {item.currency}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {item.activePhoneNumbers} x {formatCents(item.phoneMonthlyFeeInCents)} {item.currency}
                        </TableCell>
                        <TableCell className="whitespace-nowrap font-bold">
                          {formatCents(item.calculatedTotalInCents)} {item.currency}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(item.currentPeriodEnd)}
                        </TableCell>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDetailItem(item)}
                                aria-label={`Details fuer ${item.customerName}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Details anzeigen</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <p>
              Seite {page} von {totalPages} ({data.total} Eintraege)
            </p>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isLoading}
              >
                Zurueck
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isLoading}
              >
                Weiter
              </Button>
            </div>
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailItem !== null} onOpenChange={(open) => { if (!open) setDetailItem(null); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Abrechnungsdetails - {detailItem?.customerName}
              </DialogTitle>
              <DialogDescription>
                Detaillierte Abrechnungsinformationen fuer das aktuelle Abrechnungsperiode
              </DialogDescription>
            </DialogHeader>

            {detailItem && (
              <div className="space-y-4">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="periods">
                      <History className="w-4 h-4 mr-2" />Perioden
                    </TabsTrigger>
                    <TabsTrigger value="breakdown">
                      <FileText className="w-4 h-4 mr-2" />Monatliche Aufschluesselung
                    </TabsTrigger>
                  </TabsList>

                  {/* ── Tab: Perioden (existing view) ── */}
                  <TabsContent value="periods">
                    {(() => {
                      const selectedSnapshot: BillingPeriodSnapshot | null =
                        selectedPeriod !== 'current'
                          ? (billingHistory?.snapshots.find((s) => s.id === selectedPeriod) ?? null)
                          : null;

                      const displayData = selectedSnapshot
                        ? {
                            baseFeeInCents: selectedSnapshot.baseFeeInCents,
                            pricePerMinuteInCents: selectedSnapshot.pricePerMinuteInCents,
                            pricePerChatInCents: selectedSnapshot.pricePerChatInCents,
                            phoneMonthlyFeeInCents: selectedSnapshot.phoneMonthlyFeeInCents,
                            currency: selectedSnapshot.currency,
                            usedCallSeconds: selectedSnapshot.usedCallSeconds,
                            usedConversations: selectedSnapshot.usedConversations,
                            usedTestRuns: selectedSnapshot.usedTestRuns,
                            activePhoneNumbers: selectedSnapshot.activePhoneNumbers,
                            calculatedTotalInCents: selectedSnapshot.calculatedTotalInCents,
                            pricePerExternalForwardingInCents: selectedSnapshot.pricePerExternalForwardingInCents,
                            usedExternalForwardings: selectedSnapshot.usedExternalForwardings,
                          }
                        : {
                            baseFeeInCents: detailItem.baseFeeInCents,
                            pricePerMinuteInCents: detailItem.pricePerMinuteInCents,
                            pricePerChatInCents: detailItem.pricePerChatInCents,
                            phoneMonthlyFeeInCents: detailItem.phoneMonthlyFeeInCents,
                            currency: detailItem.currency,
                            usedCallSeconds: detailItem.usedCallSeconds,
                            usedConversations: detailItem.usedConversations,
                            usedTestRuns: detailItem.usedTestRuns,
                            activePhoneNumbers: detailItem.activePhoneNumbers,
                            calculatedTotalInCents: detailItem.calculatedTotalInCents,
                            pricePerExternalForwardingInCents: detailItem.pricePerExternalForwardingInCents,
                            usedExternalForwardings: detailItem.usedExternalForwardings,
                          };

                      return (
                        <div className="space-y-6">
                          {/* Period selector */}
                          <div className="flex items-center space-x-2 mb-4">
                            <History className="w-4 h-4 text-gray-500" />
                            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                              <SelectTrigger className="w-72">
                                <SelectValue placeholder="Abrechnungsperiode waehlen" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="current">Aktuelle Periode</SelectItem>
                                {billingHistory?.snapshots.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>
                                    {formatDate(s.periodStart)} - {formatDate(s.periodEnd)}{s.planName && ` (${s.planName})`}{s.snapshotType === 'plan_change' && ' - Planwechsel'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {isLoadingHistory && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                          </div>

                          {/* Abonnement-Info */}
                          <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">Abonnement-Info</h3>
                            <div className="flex flex-wrap gap-3">
                              <div className="bg-gray-50 rounded-md px-3 py-2">
                                <p className="text-xs text-gray-500">Plan</p>
                                <p className="text-sm font-medium">
                                  {selectedSnapshot?.planName ?? detailItem.planName}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-md px-3 py-2">
                                <p className="text-xs text-gray-500">Status</p>
                                <div className="mt-0.5">{getStatusBadge(detailItem.status)}</div>
                              </div>
                              <div className="bg-gray-50 rounded-md px-3 py-2">
                                <p className="text-xs text-gray-500">Zeitraum</p>
                                <p className="text-sm font-medium">
                                  {selectedSnapshot
                                    ? <>{formatDate(selectedSnapshot.periodStart)} &ndash; {formatDate(selectedSnapshot.periodEnd)}</>
                                    : <>{formatDate(detailItem.currentPeriodStart)} &ndash; {formatDate(detailItem.currentPeriodEnd)}</>
                                  }
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-md px-3 py-2">
                                <p className="text-xs text-gray-500">Waehrung</p>
                                <p className="text-sm font-medium">{displayData.currency}</p>
                              </div>
                            </div>
                          </div>

                          {/* Kostenaufstellung */}
                          <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">Kostenaufstellung</h3>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Position</TableHead>
                                  <TableHead className="text-right">Menge</TableHead>
                                  <TableHead className="text-right">Einzelpreis</TableHead>
                                  <TableHead className="text-right">Summe</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                <TableRow>
                                  <TableCell>Grundgebuehr</TableCell>
                                  <TableCell className="text-right">1</TableCell>
                                  <TableCell className="text-right">
                                    {formatCents(displayData.baseFeeInCents)} {displayData.currency}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {formatCents(displayData.baseFeeInCents)} {displayData.currency}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Gespraechsminuten</TableCell>
                                  <TableCell className="text-right">{formatSecondsAsMmSs(displayData.usedCallSeconds)} ({secondsToBilledMinutes(displayData.usedCallSeconds)} min)</TableCell>
                                  <TableCell className="text-right">
                                    {formatCents(displayData.pricePerMinuteInCents)} {displayData.currency}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {formatCents(secondsToBilledMinutes(displayData.usedCallSeconds) * displayData.pricePerMinuteInCents)} {displayData.currency}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Konversationen</TableCell>
                                  <TableCell className="text-right">{displayData.usedConversations}</TableCell>
                                  <TableCell className="text-right">
                                    {formatCents(displayData.pricePerChatInCents)} {displayData.currency}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {formatCents(displayData.usedConversations * displayData.pricePerChatInCents)} {displayData.currency}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Telefonnummern (monatl.)</TableCell>
                                  <TableCell className="text-right">{displayData.activePhoneNumbers}</TableCell>
                                  <TableCell className="text-right">
                                    {formatCents(displayData.phoneMonthlyFeeInCents)} {displayData.currency}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {formatCents(displayData.activePhoneNumbers * displayData.phoneMonthlyFeeInCents)} {displayData.currency}
                                  </TableCell>
                                </TableRow>
                                {(displayData.usedExternalForwardings > 0 || displayData.pricePerExternalForwardingInCents > 0) && (
                                  <TableRow>
                                    <TableCell>Ext. Weiterleitungen</TableCell>
                                    <TableCell className="text-right">{displayData.usedExternalForwardings}</TableCell>
                                    <TableCell className="text-right">
                                      {formatCents(displayData.pricePerExternalForwardingInCents)} {displayData.currency}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCents(displayData.usedExternalForwardings * displayData.pricePerExternalForwardingInCents)} {displayData.currency}
                                    </TableCell>
                                  </TableRow>
                                )}
                                <TableRow className="font-bold bg-gray-50">
                                  <TableCell colSpan={3}>Gesamtbetrag</TableCell>
                                  <TableCell className="text-right font-bold">
                                    {formatCents(displayData.calculatedTotalInCents)} {displayData.currency}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>

                          {/* Nutzung */}
                          <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">Nutzung</h3>
                            <div className="grid grid-cols-3 gap-3">
                              <Card className="border-blue-200 bg-blue-50">
                                <CardContent className="p-4">
                                  <p className="text-xs text-blue-600 font-medium">Telefonzeit</p>
                                  <p className="text-2xl font-bold text-blue-800 mt-1">
                                    {formatSecondsAsMmSs(displayData.usedCallSeconds)}
                                  </p>
                                </CardContent>
                              </Card>
                              <Card className="border-green-200 bg-green-50">
                                <CardContent className="p-4">
                                  <p className="text-xs text-green-600 font-medium">Konversationen</p>
                                  <p className="text-2xl font-bold text-green-800 mt-1">
                                    {displayData.usedConversations.toLocaleString('de-DE')}
                                  </p>
                                </CardContent>
                              </Card>
                              <Card className="border-purple-200 bg-purple-50">
                                <CardContent className="p-4">
                                  <p className="text-xs text-purple-600 font-medium">Testruns</p>
                                  <p className="text-2xl font-bold text-purple-800 mt-1">
                                    {displayData.usedTestRuns.toLocaleString('de-DE')}
                                  </p>
                                </CardContent>
                              </Card>
                            </div>
                          </div>

                          {/* Telefonnummern */}
                          {phonePurchases.length > 0 && (
                            <div>
                              <h3 className="text-sm font-semibold text-gray-700 mb-3">Telefonnummern</h3>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Nummer</TableHead>
                                    <TableHead>Einrichtung</TableHead>
                                    <TableHead>Monatspreis</TableHead>
                                    <TableHead>Kaufdatum</TableHead>
                                    <TableHead>Status</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {phonePurchases.map((p) => (
                                    <TableRow key={p.id}>
                                      <TableCell className="font-mono text-sm">{p.phoneNumber}</TableCell>
                                      <TableCell>{formatCents(p.setupFeeInCents)} {p.currency}</TableCell>
                                      <TableCell>{formatCents(p.monthlyFeeInCents)} {p.currency}</TableCell>
                                      <TableCell>{new Date(p.purchasedAt).toLocaleString('de-DE')}</TableCell>
                                      <TableCell>
                                        {p.releasedAt
                                          ? <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">Freigegeben</Badge>
                                          : <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Aktiv</Badge>
                                        }
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </TabsContent>

                  {/* ── Tab: Monatliche Aufschluesselung ── */}
                  <TabsContent value="breakdown">
                    <div className="space-y-6">
                      {/* Month/Year selector */}
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <Select
                          value={String(breakdownMonth)}
                          onValueChange={(v) => setBreakdownMonth(Number(v))}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[
                              'Januar', 'Februar', 'Maerz', 'April', 'Mai', 'Juni',
                              'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
                            ].map((name, i) => (
                              <SelectItem key={i + 1} value={String(i + 1)}>{name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={String(breakdownYear)}
                          onValueChange={(v) => setBreakdownYear(Number(v))}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isLoadingBreakdown && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      </div>

                      {isLoadingBreakdown ? (
                        <div className="flex items-center justify-center h-32">
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                      ) : breakdownData ? (
                        <>
                          {/* Plan segments */}
                          <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">
                              Plansegmente
                              {breakdownData.planSegments.length > 1 && (
                                <span className="text-xs font-normal text-gray-500 ml-2">
                                  ({breakdownData.planSegments.length} Segmente, {breakdownData.daysInMonth} Tage)
                                </span>
                              )}
                            </h3>
                            <div className="relative border rounded-md" style={{ maxHeight: 400, overflowY: 'auto' }}>
                              <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                <thead>
                                  <tr>
                                    {['Zeitraum', 'Plan', 'Tage', 'Grundgebuehr', 'Voice', 'Chat', 'Summe'].map((label, i) => (
                                      <th
                                        key={label}
                                        className={`h-10 px-2 font-medium whitespace-nowrap border-b bg-white ${i < 2 ? 'text-left' : 'text-right'}`}
                                        style={{ position: 'sticky', top: 0, zIndex: 10 }}
                                      >
                                        {label}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="[&_tr:last-child]:border-0">
                                  {[...breakdownData.planSegments].reverse().map((seg, idx) => (
                                    <tr key={idx} className="border-b transition-colors hover:bg-muted/50">
                                      <td className="p-2 align-middle whitespace-nowrap text-sm">
                                        {formatDate(seg.from)} &ndash; {formatDate(seg.to)}
                                      </td>
                                      <td className="p-2 align-middle text-sm">{seg.planName}</td>
                                      <td className="p-2 align-middle text-right text-sm">{Math.round(seg.days * 10) / 10}</td>
                                      <td className="p-2 align-middle text-right text-sm">
                                        {formatCents(seg.proRataBaseFeeInCents)} {breakdownData.currency}
                                      </td>
                                      <td className="p-2 align-middle text-right text-sm">
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger className="cursor-default">
                                              {formatCents(seg.voiceCostInCents)} {breakdownData.currency}
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              {seg.voiceMinutes} Min x {formatCents(seg.pricing.pricePerMinuteInCents)} {breakdownData.currency}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      </td>
                                      <td className="p-2 align-middle text-right text-sm">
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger className="cursor-default">
                                              {formatCents(seg.chatCostInCents)} {breakdownData.currency}
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              {seg.chatSessions} x {formatCents(seg.pricing.pricePerChatInCents)} {breakdownData.currency}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      </td>
                                      <td className="p-2 align-middle text-right text-sm font-medium">
                                        {formatCents(seg.segmentTotalInCents)} {breakdownData.currency}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Phone numbers */}
                          {breakdownData.phoneNumbers.length > 0 && (
                            <div>
                              <h3 className="text-sm font-semibold text-gray-700 mb-3">Telefonnummern</h3>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Nummer</TableHead>
                                    <TableHead>Kaufdatum</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Aktive Tage</TableHead>
                                    <TableHead className="text-right">Einrichtung</TableHead>
                                    <TableHead className="text-right">Monatl. (anteilig)</TableHead>
                                    <TableHead className="text-right">Summe</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {breakdownData.phoneNumbers.map((pn, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="font-mono text-sm">{pn.phoneNumber}</TableCell>
                                      <TableCell className="text-sm">{new Date(pn.purchasedAt).toLocaleString('de-DE')}</TableCell>
                                      <TableCell>
                                        {pn.releasedAt
                                          ? <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">Freigegeben</Badge>
                                          : <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Aktiv</Badge>
                                        }
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {Math.round(pn.activeDaysInMonth * 10) / 10}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {pn.setupFeeInCents > 0
                                          ? `${formatCents(pn.setupFeeInCents)} ${breakdownData.currency}`
                                          : '-'
                                        }
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {formatCents(pn.proRataMonthlyFeeInCents)} {breakdownData.currency}
                                      </TableCell>
                                      <TableCell className="text-right text-sm font-medium">
                                        {formatCents(pn.totalCostInCents)} {breakdownData.currency}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}

                          {/* External forwardings */}
                          {breakdownData.externalForwardings.count > 0 && (
                            <div>
                              <h3 className="text-sm font-semibold text-gray-700 mb-3">Ext. Weiterleitungen</h3>
                              <div className="bg-gray-50 rounded-md px-4 py-3 text-sm flex items-center justify-between">
                                <span>
                                  {breakdownData.externalForwardings.count} x {formatCents(breakdownData.externalForwardings.pricePerForwardingInCents)} {breakdownData.currency}
                                </span>
                                <span className="font-medium">
                                  {formatCents(breakdownData.externalForwardings.totalCostInCents)} {breakdownData.currency}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Summary */}
                          <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">Zusammenfassung</h3>
                            <Card className="border-gray-200">
                              <CardContent className="p-4 space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Grundgebuehren</span>
                                  <span>{formatCents(breakdownData.summary.totalBaseFeeInCents)} {breakdownData.currency}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Voice-Kosten</span>
                                  <span>{formatCents(breakdownData.summary.totalVoiceCostInCents)} {breakdownData.currency}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Chat-Kosten</span>
                                  <span>{formatCents(breakdownData.summary.totalChatCostInCents)} {breakdownData.currency}</span>
                                </div>
                                {breakdownData.summary.totalPhoneSetupFeesInCents > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Tel. Einrichtung</span>
                                    <span>{formatCents(breakdownData.summary.totalPhoneSetupFeesInCents)} {breakdownData.currency}</span>
                                  </div>
                                )}
                                {breakdownData.summary.totalPhoneMonthlyFeesInCents > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Tel. Monatsgebuehren</span>
                                    <span>{formatCents(breakdownData.summary.totalPhoneMonthlyFeesInCents)} {breakdownData.currency}</span>
                                  </div>
                                )}
                                {breakdownData.summary.totalExternalForwardingCostInCents > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Ext. Weiterleitungen</span>
                                    <span>{formatCents(breakdownData.summary.totalExternalForwardingCostInCents)} {breakdownData.currency}</span>
                                  </div>
                                )}
                                <div className="border-t pt-2 mt-2 flex justify-between font-bold text-base">
                                  <span>Gesamtbetrag</span>
                                  <span>{formatCents(breakdownData.summary.grandTotalInCents)} {breakdownData.currency}</span>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
                          Keine Daten verfuegbar
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Billing address and close button (outside tabs) */}
                {detailItem.billingAddress && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Rechnungsanschrift</h3>
                    <div className="bg-gray-50 rounded-md px-4 py-3 text-sm space-y-0.5">
                      {detailItem.billingAddress.company && (
                        <p className="font-medium">{detailItem.billingAddress.company}</p>
                      )}
                      {detailItem.billingAddress.name && (
                        <p>{detailItem.billingAddress.name}</p>
                      )}
                      {detailItem.billingAddress.street && (
                        <p>{detailItem.billingAddress.street}</p>
                      )}
                      {(detailItem.billingAddress.zip || detailItem.billingAddress.city) && (
                        <p>
                          {detailItem.billingAddress.zip} {detailItem.billingAddress.city}
                        </p>
                      )}
                      {detailItem.billingAddress.country && (
                        <p>{detailItem.billingAddress.country}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setDetailItem(null)}>
                    Schliessen
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
